import { getEnValue, getZhValue } from '@/../../common/src/utils/index';
import { CreFitAppVersion } from '@/../../common/src/utils/nativeApi/interface';
import { commonConfig, queryClAvaliable, queryUserAvaliable } from '@/appApi';
import { MarketingPlanOption } from '@/interface/CL/clCredit';
import { CouponType } from '@/interface/KKC/coupon';
import { BoolString } from '@/interface/KKC/withdrawal';
import type { ProtocolType } from '@/interface/pub';
import { Decimal } from 'decimal.js';
import i18next, { t } from 'i18next';
import { Toast } from 'rc-ui-lib';
import semver from 'semver';
import { encrypt } from './Crypto';
import { getGlobalUserInfo } from './singletonUtils';

export const vhkUrlPrefix = 'vhk';

export const bmUrlPrefix = 'vhk';

export const mailAfterFix = ['gmail.com', 'outlook.com', 'yahoo.com', 'yahoo.com.hk', 'icloud.com'];

export const moneyFormat = (val: number | string | Decimal) => {
  const c =
    Number(val).toString().indexOf('.') !== -1
      ? Number(val).toLocaleString()
      : Number(val)
          .toString()
          .replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
  return c;
};

export function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& 表示匹配的字符
}

export const englishFormatter = (value: string | number) => {
  const str = String(value);
  const regRule = /[\u4e00-\u9fa5]/g;
  if (regRule.test(str)) {
    Toast.info({
      message: t<string, string>('clcreditv2.credit.rule_only_english'),
    });
  }
  return str.replace(regRule, '');
};

export const engAndNumFormatter = (value: string | number) =>
  String(value)
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();

export const isEn = () => i18next.language === 'en';

export const handleEnClsName = (clsName: string) => `${clsName} ${isEn() ? 'en' : ''}`;

export const getCommonConfig = (strs: string[]) => commonConfig(encrypt(JSON.stringify(strs)));

export const getSpecificBankName = (bankname: string) => {
  if (bankname === 'HSENG') {
    return 'HSB';
  }
  if (bankname === 'PRIMECREDIT') {
    return 'PRIME\nCREDIT';
  }
  return bankname;
};

export const EddaCardsNum = 5;

let cacheClAvaliable: boolean | undefined;
let cacheCmcAvaliable: boolean | undefined;

export const isClAvaliable = async () => {
  if (cacheClAvaliable === undefined) {
    const user = await getGlobalUserInfo();
    const result = await queryClAvaliable(user.userId || '0');
    cacheClAvaliable = result === BoolString.YES;
  }
  return cacheClAvaliable;
};

export const isCmcAvaliable = async () => {
  if (cacheCmcAvaliable === undefined) {
    const user = await getGlobalUserInfo();
    const result = await queryUserAvaliable(user.userId || '0', '03');
    cacheCmcAvaliable = result === BoolString.YES;
  }
  return cacheCmcAvaliable;
};

export const getValueByLang = (value: string, lang?: string) =>
  lang === 'en' ? getEnValue(value) : lang === 'zh' ? getZhValue(value) : t(value);

export const getValueByKey = (object: object, value: string | ProtocolType) =>
  Object.keys(object).find((key) => object[key as keyof object] === value);

/* eslint-disable */
export const filterEmoji = (value: string) => {
  let regEmoji =
    /[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF][\u200D|\uFE0F]|[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF]|[0-9|*|#]\uFE0F\u20E3|[0-9|#]\u20E3|[\u203C-\u3299]\uFE0F\u200D|[\u203C-\u3299]\uFE0F|[\u2122-\u2B55]|\u303D|[\A9|\AE]\u3030|\uA9|\uAE|\u3030/gi;
  return value.replace(regEmoji, '');
};
/* eslint-enable */

export const facebookPixelId1 = '1357739888159653';
export const facebookPixelId2 = '1078816106823027';
export const facebookPixelId3 = '297212789712855';

export const isVerGTE = (curVer: CreFitAppVersion, targetVer: CreFitAppVersion) =>
  semver.gte(curVer.toString(), targetVer.toString());

// 使用 filter 方法篩選出在 min 和 max 範圍內的值
export const filterPeriods = (periodList: number[], minValue: number, maxValue: number) =>
  periodList.filter((period) => period >= minValue && period <= maxValue);

// 阿里云 oss 前缀
const ossPrefix = import.meta.env.VITE_OSS_PREFIX;
export const prefixUrl = (path: string) => {
  // 如果是http开头的内容直接返回
  if (path.startsWith('http')) {
    return path;
  }
  return ossPrefix + '/' + path;
};

export const REG_CHINESE = /([\u4E00-\u9FA5]+)|([\u4E00-\u9FA5]+)/;

/**
 * bycursor-start
 * 格式化数值，自动处理小数位显示
 * @param value - 要格式化的数值
 * @param decimals - 最大小数位数，默认为2
 * @returns 格式化后的字符串
 * @example
 * formatDecimal(36.00) => "36"
 * formatDecimal(36.10) => "36.1"
 * formatDecimal(36.21) => "36.21"
 * @author bycursor
 */
export const formatDecimal = (value: number | string, decimals = 2): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return '0';
  }

  // 先固定小数位
  const fixed = num.toFixed(decimals);

  // 转换为数字再转回字符串，自动去除末尾的0
  return parseFloat(fixed).toString();
};
// bycursor-end

export const getCouponName = (
  couponType: CouponType,
  couponValue: number,
  couponContent: string
) => {
  const isShowHKprefixType =
    couponType === CouponType.ReduceInterestCoupon || couponType === CouponType.CashCoupon;

  const isShowPercentageType =
    couponType === CouponType.DiscountCoupon || couponType === CouponType.InterestRewardCoupon;

  return couponValue
    ? `${
        couponValue && isShowPercentageType
          ? `${couponValue * 100}% `
          : couponValue && isShowHKprefixType
          ? `HK$${couponValue} `
          : couponValue
      }${couponContent}`
    : null;
};

// 获取营销方案的期数选项
export const getMarketingMaturityOptions = (recommendationMap: {
  [key: string]: MarketingPlanOption;
}) => {
  const planKeys = Object.keys(recommendationMap || {});

  return planKeys.map((key) => ({
    maturity: recommendationMap[key].loanMaturity,
    monthlyAmount: recommendationMap[key].monthlyRepaymentAmount || 0,
    planId: recommendationMap[key].id,
    planType: recommendationMap[key].planType,
  }));
};
